import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format, subDays } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { AlertAction, AlertType, DisplayLocation, setAlerts } from '@app/redux/alerts';
import { selectBillingAccount } from '@app/redux/billingInfo';
import { getNextBillCycleDate } from '@app/utils';

import { BillingAccount, NotificationLevel } from '@mfe/shared/schema-types';

export const useDisplayDisconnectAlert = () => {
  const { t } = useTranslation('Disconnect');
  const dispatch = useDispatch();
  const billingAccount = useSelector(selectBillingAccount);
  const nextBillingCycleDate = getNextBillCycleDate(billingAccount);
  const secondToLastDay = getSecondToLastDay(billingAccount);

  const displayDisconnectAlert = useCallback(
    () =>
      dispatch(
        setAlerts([
          {
            level: NotificationLevel.Warning,
            title: `${t('cancelOrder.title')}`,
            caption: `${t('cancelOrder.label', {
              nextBillingCycleDate: nextBillingCycleDate,
              secondToLastDay: secondToLastDay,
            })}`,
            button: {
              action: AlertAction.ResumeService,
              label: `${t('cancelOrder.button')}`,
            },
            displayLocation: DisplayLocation.GLOBAL,
            type: AlertType.CancelOrder,
          },
        ])
      ),
    [dispatch, t, nextBillingCycleDate, secondToLastDay]
  );

  return displayDisconnectAlert;
};

function getSecondToLastDay(billingAccount: BillingAccount) {
  const previousBillingCycleDate = billingAccount?.nextBillPeriodStartDate
    ? format(subDays(new Date(billingAccount.nextBillPeriodStartDate), 1), 'MMM dd')
    : '';
  return previousBillingCycleDate;
}
